/**
 * Slot Definition Helpers
 *
 * Helpers for building SlotRegistry objects.
 * Slots are organized by LOCATION, with each location containing an array
 * of control definitions.
 */

import {type ControlDefinition, type LabelPosition} from '../types';

// ============================================================================
// Control Definition Helpers
// ============================================================================

/**
 * Creates a step slider control definition.
 *
 * @param linked - Whether control is linked across all tiles (default: true)
 * @returns Control definition for step slider
 */
export function stepSlider(): ControlDefinition {
  return {
    control: 'step',
    type: 'component',
    labelPosition: 'inline',
  };
}

/**
 * Creates an index slider control definition.
 *
 * @param linked - Whether control is linked across all tiles (default: true)
 * @returns Control definition for index slider
 */
export function indexSlider(): ControlDefinition {
  return {
    control: 'index',
    type: 'component',
    labelPosition: 'inline',
  };
}

/**
 * Creates a media key selector control definition (single-select).
 *
 * @returns Control definition for media key single selector
 */
export function mediaKeySelectorSingle(): ControlDefinition {
  return {
    control: 'mediaKey',
    type: 'component',
    selectionMode: 'single',
  };
}

/**
 * Creates a media key selector control definition (multi-select).
 *
 * @returns Control definition for media key multi selector
 */
export function mediaKeySelectorMulti(): ControlDefinition {
  return {
    control: 'mediaKey',
    type: 'component',
    selectionMode: 'multi',
  };
}

/**
 * Creates a media key card control definition (interactive card).
 *
 * Renders the media key(s) in an interactive card that opens a searchable dropdown.
 * Use this for fullscreen view or when you want richer interaction.
 *
 * @param selectionMode - Whether to allow single or multi selection
 * @param clickBehavior - How the card should open ('swap' or 'popover')
 * @returns Control definition for media key card
 */
export function mediaKeyCard(
  selectionMode: 'single' | 'multi' = 'single',
  clickBehavior: 'swap' | 'popover' = 'swap'
): ControlDefinition {
  return {
    control: 'mediaKey',
    type: 'component',
    variant: 'card',
    selectionMode,
    labelPosition: 'value',
    cardClickBehavior: clickBehavior,
  };
}

/**
 * Creates a media key selector control definition (legacy - infers mode from context).
 *
 * @deprecated Use mediaKeySelectorSingle() or mediaKeySelectorMulti() instead for explicit control
 * @returns Control definition for media key selector
 */
export function mediaKeySelector(): ControlDefinition {
  return {
    control: 'mediaKey',
    type: 'component',
  };
}

/**
 * Creates a run label control definition (simple display).
 *
 * Renders the run name as a simple label without card wrapper.
 * Use this for workspace view where you want minimal UI.
 *
 * @returns Control definition for run label
 */
export function runDropdown(): ControlDefinition {
  return {
    control: 'run',
    type: 'component',
    labelPosition: 'value',
  };
}

/**
 * Creates a run card control definition (interactive card).
 *
 * Renders the run in an interactive card that opens a searchable dropdown.
 * Use this for fullscreen view or when you want richer interaction.
 *
 * @param clickBehavior - How the card should open ('swap' or 'popover')
 * @returns Control definition for run card
 */
export function runCard(
  clickBehavior: 'swap' | 'popover' = 'swap'
): ControlDefinition {
  return {
    control: 'run',
    type: 'component',
    variant: 'card',
    labelPosition: 'value',
    cardClickBehavior: clickBehavior,
  };
}

/**
 * Creates an x-axis range slider control definition (SelectableAxis).
 *
 * Range sliders always affect all tiles (linked=true).
 *
 * @returns Control definition for x-axis range slider
 */
export function xAxisRangeSlider(): ControlDefinition {
  return {
    control: 'xAxisRange',
    type: 'component',
  };
}

/**
 * Creates a y-axis range slider control definition (SelectableAxis).
 *
 * Range sliders always affect all tiles (linked=true).
 *
 * @returns Control definition for y-axis range slider
 */
export function yAxisRangeSlider(): ControlDefinition {
  return {
    control: 'yAxisRange',
    type: 'component',
  };
}

/**
 * Creates the compare mode entry action.
 *
 * @returns Control definition for the compare entry button
 */
export function compareEntry(): ControlDefinition {
  return {
    control: 'compareEntry',
    type: 'component',
  };
}

/**
 * Creates a label control definition.
 *
 * Label controls are not rendered as interactive controls, but are shown as static
 * text labels in headers/footers to display dimension values (run name, step, index).
 *
 * @param control - Which dimension to show as a label
 * @param labelPosition - How to position the label relative to the value
 * @param classNames - Optional class names to be applied to the control
 * @returns Control definition for label control
 */
export function label(
  control: 'step' | 'index' | 'run' | 'mediaKey',
  labelPosition: LabelPosition = 'inline',
  classNames?: string
): ControlDefinition {
  return {
    control,
    type: 'label',
    classNames: classNames,
    labelPosition: labelPosition,
  };
}

/**
 * Creates a caption control definition.
 *
 * Caption controls render media captions in the tile-footer slot.
 *
 * @returns Control definition for caption control
 */
export function caption(): ControlDefinition {
  return {
    control: 'caption',
    type: 'label',
  };
}
